export interface PlantSpecies {
  id: string;
  name: string;
  type: 'day' | 'night' | 'both';
  growthTime: number;       // seconds for each growth stage
  waterNeeds: number;       // 1-5 scale
  attackPower: number;
  defensePower: number;
  specialAbility: string;
  description: string;
  spriteRow: number;        // row in the plants sprite sheet
}

export const PLANTS: Record<string, PlantSpecies> = {
  sunflower: {
    id: 'sunflower',
    name: 'Sunflower',
    type: 'day',
    growthTime: 30,
    waterNeeds: 3,
    attackPower: 15,
    defensePower: 10,
    specialAbility: 'Solar Beam',
    description:
      'A radiant flower that thrives under sunlight. Channels the power of the sun into a devastating beam of energy.',
    spriteRow: 0,
  },
  moonbloom: {
    id: 'moonbloom',
    name: 'Moonbloom',
    type: 'night',
    growthTime: 40,
    waterNeeds: 2,
    attackPower: 10,
    defensePower: 25,
    specialAbility: 'Lunar Shield',
    description:
      'A luminescent blossom that unfurls only under moonlight. Generates a protective barrier woven from lunar energy.',
    spriteRow: 1,
  },
  fire_pepper: {
    id: 'fire_pepper',
    name: 'Fire Pepper',
    type: 'day',
    growthTime: 35,
    waterNeeds: 4,
    attackPower: 30,
    defensePower: 5,
    specialAbility: 'Flame Burst',
    description:
      'An intensely hot pepper that combusts when fully ripe. Launches a burst of searing flames at nearby enemies.',
    spriteRow: 2,
  },
  shadow_vine: {
    id: 'shadow_vine',
    name: 'Shadow Vine',
    type: 'night',
    growthTime: 45,
    waterNeeds: 1,
    attackPower: 25,
    defensePower: 10,
    specialAbility: 'Shadow Snare',
    description:
      'A creeping vine that slithers through darkness. Ensnares foes with tendrils of living shadow, binding them in place.',
    spriteRow: 3,
  },
  apple_tree: {
    id: 'apple_tree',
    name: 'Apple Tree',
    type: 'day',
    growthTime: 60,
    waterNeeds: 3,
    attackPower: 18,
    defensePower: 18,
    specialAbility: 'Apple Barrage',
    description:
      'A sturdy tree bearing enchanted apples. Hurls a rapid volley of hardened fruit at anything that threatens the farm.',
    spriteRow: 4,
  },
  pear_tree: {
    id: 'pear_tree',
    name: 'Pear Tree',
    type: 'day',
    growthTime: 55,
    waterNeeds: 3,
    attackPower: 10,
    defensePower: 28,
    specialAbility: 'Pear Shield',
    description:
      'A resilient tree with thick bark and iron-hard pears. Creates a defensive wall of dense fruit to absorb incoming damage.',
    spriteRow: 5,
  },

  // === HYBRID PLANTS (created via combining) ===
  peapple: {
    id: 'peapple',
    name: 'Peapple',
    type: 'day',
    growthTime: 50,
    waterNeeds: 3,
    attackPower: 22,
    defensePower: 30,
    specialAbility: 'Mutant Barrage',
    description:
      'A bizarre mutant hybrid of apple and pear. Its misshapen fruit explodes on impact.',
    spriteRow: 6,
  },
  twilight_blossom: {
    id: 'twilight_blossom',
    name: 'Twilight Blossom',
    type: 'both',
    growthTime: 45,
    waterNeeds: 3,
    attackPower: 20,
    defensePower: 20,
    specialAbility: 'Eclipse Pulse',
    description:
      'Born from the union of sun and moon. Emits pulses of twilight energy.',
    spriteRow: 7,
  },
  infernal_creeper: {
    id: 'infernal_creeper',
    name: 'Infernal Creeper',
    type: 'both',
    growthTime: 55,
    waterNeeds: 2,
    attackPower: 40,
    defensePower: 8,
    specialAbility: 'Hellfire Lash',
    description:
      'A terrifying fusion of fire and darkness. Its burning tendrils deal extreme damage.',
    spriteRow: 8,
  },
  solar_flare: {
    id: 'solar_flare',
    name: 'Solar Flare',
    type: 'day',
    growthTime: 40,
    waterNeeds: 5,
    attackPower: 45,
    defensePower: 5,
    specialAbility: 'Corona Eruption',
    description:
      'The fury of the sun. Channels concentrated solar energy into a massive eruption.',
    spriteRow: 9,
  },
  void_orchid: {
    id: 'void_orchid',
    name: 'Void Orchid',
    type: 'night',
    growthTime: 50,
    waterNeeds: 1,
    attackPower: 12,
    defensePower: 42,
    specialAbility: 'Abyssal Ward',
    description:
      'Blooms in absolute darkness. Generates an impenetrable ward of void energy.',
    spriteRow: 10,
  },
};

// Items available at the seed shop
export interface ShopItem {
  id: string;
  name: string;
  cost: number;
  description: string;
  category: 'seed' | 'tool';
}

export const SHOP_ITEMS: ShopItem[] = [
  { id: 'sunflower', name: 'Sunflower Seeds', cost: 3, description: 'Basic day plant', category: 'seed' },
  { id: 'moonbloom', name: 'Moonbloom Seeds', cost: 5, description: 'Night flower', category: 'seed' },
  { id: 'fire_pepper', name: 'Fire Pepper Seeds', cost: 4, description: 'Explosive plant', category: 'seed' },
  { id: 'shadow_vine', name: 'Shadow Vine Seeds', cost: 5, description: 'Dark vine', category: 'seed' },
  { id: 'apple_tree', name: 'Apple Seeds', cost: 6, description: 'Day tree', category: 'seed' },
  { id: 'pear_tree', name: 'Pear Seeds', cost: 6, description: 'Defense tree', category: 'seed' },
  { id: '_fertilizer', name: 'Fertilizer x3', cost: 8, description: '2x growth', category: 'tool' },
  { id: '_sprinkler', name: 'Sprinkler', cost: 20, description: 'Auto-waters', category: 'tool' },
];
